import { TypedDocumentNode } from '@apollo/client'
import { faBuilding, faPen, faPlus, faUser } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconButton, Stack, Typography } from '@mui/material'
import React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CustomerDetailFragment, CustomerType, IDFilter, StringFilter } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import CustomerDetail from 'src/app/pages/common/customer/customer-detail'
import QueryLookup, { isId } from '../../query-lookup/query-lookup'

export type BaseCustomerLookup = {
  id: string
  type: CustomerType
  name: string
}

export type BaseCustomerLookupQuery<T extends BaseCustomerLookup> = {
  customers?: T[] | null
}

export type CustomerLookupQueryVariables = {
  tenantId: string
  search?: StringFilter
  idFilter?: IDFilter
  type?: CustomerType
  skip?: number
  limit?: number
}

export type BaseCustomerLookupProps<T extends BaseCustomerLookup> = {
  customerType?: CustomerType
  value: T | { id: string } | null
  onChange: (value: T | null) => void
  queryDocument: TypedDocumentNode<BaseCustomerLookupQuery<T>, CustomerLookupQueryVariables>
  renderOption?: (option: T) => React.ReactNode
  createAction?: boolean
  updateAction?: boolean
  onCustomerUpserted?: (customerDetail: CustomerDetailFragment) => T
  disabled?: boolean
  error?: boolean
  footerOption?: (inputValue: string, onClose: () => void) => React.ReactNode
  minSearchParameterLenght?: number
  clearOnBlur?: boolean
  onBlur?: (inputValue: string) => void
  size?: 'small' | 'medium'
  selectableCustomers?: string[]
  noOptionsText?: React.ReactNode
}

export default function BaseCustomerLookup<T extends BaseCustomerLookup>(props: BaseCustomerLookupProps<T>) {
  const { customerType, value, onChange, queryDocument, renderOption, createAction, updateAction, disabled, error, footerOption, clearOnBlur, onBlur, selectableCustomers } = props
  const tenantId = useTenant().id
  const minSearchParameterLenght = props.minSearchParameterLenght || 3

  const [localValue, setLocalValue] = useState<T | { id: string } | null>(null)
  const onLocalChange = useCallback(
    (newLocalValue: T | { id: string } | null) => {
      if (JSON.stringify(newLocalValue) !== JSON.stringify(localValue)) {
        setLocalValue(newLocalValue)
      }
      if (JSON.stringify(newLocalValue) !== JSON.stringify(value) && !isId(newLocalValue)) {
        onChange(newLocalValue)
      }
    },
    [localValue, value, onChange],
  )

  useEffect(() => {
    if (value === null) {
      onLocalChange(null)
      return
    }
    if (value.id !== localValue?.id) {
      onLocalChange(value)
      return
    }
    if ('type' in value && customerType && value.type !== customerType) {
      onLocalChange(null)
      return
    }

    if ('type' in value && JSON.stringify(value) !== JSON.stringify(localValue)) {
      onLocalChange(value)
      return
    }
  }, [value])

  useEffect(() => {
    if (localValue && 'type' in localValue && customerType && localValue.type !== customerType) {
      onLocalChange(null)
    }
  }, [customerType])

  const fetchQueryVariables = useCallback(
    (id: string) => {
      const variables: CustomerLookupQueryVariables = {
        tenantId: tenantId,
        type: customerType,
        idFilter: { eq: id },
        skip: 0,
        limit: 1,
      }
      return variables
    },
    [tenantId, customerType],
  )

  const searchQueryVariables = useCallback(
    (text: string) => {
      const variables: CustomerLookupQueryVariables = {
        tenantId: tenantId,
        type: customerType,
        search: { startsWith: text },
      }
      return variables
    },
    [tenantId, customerType],
  )

  //#region ACTIONS
  const showCreateButtonEnabled: boolean = useMemo(() => customerType !== undefined && createAction === true && localValue === null, [customerType, createAction, localValue])
  const showCreateButtonDisabled: boolean = useMemo(
    () => customerType !== undefined && createAction === true && localValue !== null && updateAction !== true,
    [createAction, localValue, updateAction, customerType],
  )

  const showUpdateButtonEnabled: boolean = useMemo(() => updateAction === true && localValue !== null, [updateAction, localValue])
  const showUpdateButtonDisabled: boolean = useMemo(
    () => updateAction === true && localValue === null && (createAction !== true || customerType === undefined),
    [createAction, localValue, updateAction, customerType],
  )

  const [customerDetailProps, setCustomerDetailProps] = useState<{ customerId: string } | { type: CustomerType } | undefined>(undefined)
  const openCreateCustomerDetail = useCallback(() => {
    if (customerType) {
      setCustomerDetailProps({ type: customerType })
    }
  }, [customerType])
  const openUpdateCustomerDetail = useCallback(() => {
    if (localValue) {
      setCustomerDetailProps({ customerId: localValue.id })
    }
  }, [localValue])
  const closeCustomerDetail = useCallback(() => {
    setCustomerDetailProps(undefined)
  }, [])
  const onUpserted = useCallback(
    (customerDetail: CustomerDetailFragment) => {
      closeCustomerDetail()
      if (props.onCustomerUpserted) {
        onLocalChange(props.onCustomerUpserted(customerDetail))
      } else {
        setLocalValue(null)
        setLocalValue({ id: customerDetail.id })
      }
    },
    [props.onCustomerUpserted, onLocalChange],
  )

  //#endregion ACTIONS

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <QueryLookup<T, BaseCustomerLookupQuery<T>, CustomerLookupQueryVariables> //
          value={localValue}
          onChange={onLocalChange}
          queryDocument={queryDocument}
          fetchQueryVariables={fetchQueryVariables}
          getFetchRecord={(data: BaseCustomerLookupQuery<T>) => data.customers?.[0] || null}
          searchQueryVariables={searchQueryVariables}
          getSearchRecords={(data: BaseCustomerLookupQuery<T>) =>
            data.customers?.filter((c) => {
              if (selectableCustomers) {
                return selectableCustomers.includes(c.id)
              }
              return true
            }) || []
          }
          getOptionLabel={(option) => option.name}
          renderOption={renderOption || defaultRenderOption}
          disabled={disabled}
          error={error}
          footerOption={footerOption}
          minSearchParameterLenght={minSearchParameterLenght}
          clearOnBlur={clearOnBlur}
          onBlur={onBlur}
          size={props.size}
          noOptionsText={props.noOptionsText}
        />
        {showCreateButtonEnabled && (
          <IconButton onClick={openCreateCustomerDetail}>
            <FontAwesomeIcon icon={faPlus} size={'xs'} />
          </IconButton>
        )}
        {showCreateButtonDisabled && (
          <IconButton disabled>
            <FontAwesomeIcon icon={faPlus} size={'xs'} />
          </IconButton>
        )}
        {showUpdateButtonEnabled && (
          <IconButton onClick={openUpdateCustomerDetail} disabled={disabled}>
            <FontAwesomeIcon icon={faPen} size={'xs'} />
          </IconButton>
        )}
        {showUpdateButtonDisabled && (
          <IconButton disabled>
            <FontAwesomeIcon icon={faPen} size={'xs'} />
          </IconButton>
        )}
      </Stack>
      {customerDetailProps && <CustomerDetailMemo {...customerDetailProps} open={customerDetailProps !== undefined} onClose={closeCustomerDetail} onUpserted={onUpserted} />}
    </>
  )
}

type CustomerDetailMemoProps =
  | { customerId: string; open: boolean; onClose: () => void; onUpserted: (customerDetail: CustomerDetailFragment) => void }
  | { type: CustomerType; open: boolean; onClose: () => void; onUpserted: (customerDetail: CustomerDetailFragment) => void }

const CustomerDetailMemo = React.memo(
  (props: CustomerDetailMemoProps) => {
    const { open, onClose, onUpserted, ...otherProps } = props
    return (
      <CustomerDetail
        {...otherProps}
        appearance={{
          open: open,
          type: 'DIALOG',
          onClose: onClose,
          maxWidth: '800px',
        }}
        onCancel={onClose}
        onUpserted={onUpserted}
      />
    )
  },
  (prev, next) => {
    if (prev.open !== next.open) {
      return false
    }
    if (prev.onClose !== next.onClose) {
      return false
    }
    if (prev.onUpserted !== next.onUpserted) {
      return false
    }
    if ('customerId' in prev) {
      if ('customerId' in next) {
        return prev.customerId === next.customerId
      } else {
        return false
      }
    } else {
      if ('type' in next) {
        return prev.type === next.type
      } else {
        return false
      }
    }
  },
)

function defaultRenderOption<T extends BaseCustomerLookup>(option: T) {
  return (
    <Stack direction="row" width="100%" alignItems="center" spacing={1}>
      <FontAwesomeIcon icon={option.type === CustomerType.PERSON ? faUser : faBuilding} />
      <Stack direction="column" width="100%">
        <Typography variant="body1">{option.name}</Typography>
      </Stack>
    </Stack>
  )
}
